import React from "react";
import { Building2, IndianRupee, Plus, Trash2 } from "lucide-react";
import { AppButton, FormField, IconButton, Input, NativeSelect } from "../../ui";
import { splitMonths } from "./studentProfileRules";

const YEARS = Array.from({ length: 41 }, (_, i) => i);
const MONTHS = Array.from({ length: 12 }, (_, i) => i);

export const EMPTY_COMPANY = { company: "", designation: "", salary: "", durationMonths: "", lastWorkingDay: "" };

/**
 * A row that is not saved yet gets a temporary id, so the documents queued
 * against it can name it. The server drops anything that is not a real id and
 * mints one, in the same order — `AddStudentPage` maps the two by position.
 */
export const isTempCompanyId = (id) => String(id || "").startsWith("tmp-");
const tempCompanyId = () => `tmp-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/**
 * WorkHistoryEditor — the companies a student worked at before: name,
 * designation, salary, how long, and the last working day. Shown when the
 * experience answer is more than zero.
 *
 * Value-first: `rows` in the stored shape, `onChange(nextRows)`. A saved row
 * keeps its `_id` — the experience documents point at it.
 */
const WorkHistoryEditor = ({ rows = [], onChange, size = "md", readOnly = false }) => {
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => onChange([...rows, { ...EMPTY_COMPANY, _id: tempCompanyId() }]);

  const setDuration = (i, part, value) => {
    const cur = splitMonths(rows[i].durationMonths);
    const next = { ...cur, [part]: Number(value) || 0 };
    update(i, { durationMonths: next.years * 12 + next.months });
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {rows.length === 0 && (
        <p className="tw-m-0 tw-text-sm tw-text-content-subtle">No previous companies added yet.</p>
      )}
      {rows.map((row, i) => {
        const d = splitMonths(row.durationMonths);
        const name = row.company || `Company ${i + 1}`;
        return (
          <div key={row._id || i} className="tw-flex tw-items-start tw-gap-3 tw-rounded-sm tw-border tw-border-solid tw-border-line tw-bg-surface-sunken tw-p-3">
            <div className="tw-grid tw-min-w-0 tw-flex-1 tw-gap-3 min-[576px]:tw-grid-cols-2">
              <FormField label="Company" required>
                <Input
                  size={size}
                  value={row.company || ""}
                  onChange={(e) => update(i, { company: e.target.value })}
                  placeholder="Company name"
                  leading={<Building2 size={14} aria-hidden="true" />}
                  readOnly={readOnly}
                  aria-label={`Company ${i + 1} name`}
                />
              </FormField>
              <FormField label="Designation">
                <Input size={size} value={row.designation || ""} onChange={(e) => update(i, { designation: e.target.value })} placeholder="e.g. Software Engineer" readOnly={readOnly} aria-label={`Designation at ${name}`} />
              </FormField>
              <FormField label="Salary (per year)">
                <Input
                  size={size}
                  inputMode="numeric"
                  value={row.salary ?? ""}
                  onChange={(e) => update(i, { salary: e.target.value })}
                  placeholder="e.g. 450000"
                  leading={<IndianRupee size={14} aria-hidden="true" />}
                  readOnly={readOnly}
                  aria-label={`Salary at ${name}`}
                />
              </FormField>
              <FormField label="Experience here">
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                  <NativeSelect size={size} value={d.years} onChange={(e) => setDuration(i, "years", e.target.value)} disabled={readOnly} aria-label={`Years at ${name}`}>
                    {YEARS.map((y) => <option key={y} value={y}>{y} yr{y === 1 ? "" : "s"}</option>)}
                  </NativeSelect>
                  <NativeSelect size={size} value={d.months} onChange={(e) => setDuration(i, "months", e.target.value)} disabled={readOnly} aria-label={`Months at ${name}`}>
                    {MONTHS.map((m) => <option key={m} value={m}>{m} mo</option>)}
                  </NativeSelect>
                </div>
              </FormField>
              <FormField label="Last working day" hint="Leave blank if still working here" className="min-[576px]:tw-col-span-2">
                <Input
                  size={size}
                  type="date"
                  value={row.lastWorkingDay ? String(row.lastWorkingDay).slice(0, 10) : ""}
                  onChange={(e) => update(i, { lastWorkingDay: e.target.value })}
                  readOnly={readOnly}
                  aria-label={`Last working day at ${name}`}
                />
              </FormField>
            </div>
            {!readOnly && (
              <IconButton
                label={`Remove ${name}`}
                icon={Trash2}
                variant="ghost-destructive"
                size="sm"
                type="button"
                className="tw-mt-6 tw-shrink-0"
                onClick={() => remove(i)}
              />
            )}
          </div>
        );
      })}
      {!readOnly && (
        <AppButton variant="outline" size="sm" type="button" onClick={add} className="tw-self-start">
          <Plus size={16} /> Add company
        </AppButton>
      )}
    </div>
  );
};

export default WorkHistoryEditor;
